/**
 * Shared module-scoped fee page (residence or mess).
 * Handles: filtered list, per-module summary cards, create-fee, record-payment,
 * combined receipt indicator. Used by /residence-fees and /mess-fees.
 */
import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, IndianRupee, Download, Search, CreditCard, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadXlsx } from "@/lib/export";
import { PaginationBar } from "@/components/PaginationBar";

const RESIDENCE_TYPES = [
  { v: "rent", l: "Room Rent" }, { v: "room_rent", l: "Room Rent (alt)" },
  { v: "bed_charge", l: "Bed Charges" }, { v: "security_deposit", l: "Security Deposit" },
  { v: "electricity", l: "Electricity" }, { v: "water", l: "Water" },
  { v: "maintenance", l: "Maintenance" }, { v: "late_penalty", l: "Late Payment Penalty" },
  { v: "residence_discount", l: "Discount" }, { v: "residence_refund", l: "Refund" },
  { v: "hostel", l: "Hostel (legacy)" },
];
const MESS_TYPES = [
  { v: "mess_monthly", l: "Monthly Mess Fee" }, { v: "breakfast", l: "Breakfast" },
  { v: "lunch", l: "Lunch" }, { v: "dinner", l: "Dinner" },
  { v: "one_time_meal", l: "One-Time Meal" }, { v: "guest_meal", l: "Guest Meal" },
  { v: "meal_coupon", l: "Meal Coupon" },
  { v: "mess_discount", l: "Discount" }, { v: "mess_refund", l: "Refund" },
  { v: "mess", l: "Mess (legacy)" },
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

export default function FeeModulePage({ module }) {
  const { user } = useAuth();
  const isResidence = module === "residence";
  const label = isResidence ? "Residence Fees" : "Mess Fees";
  const types = isResidence ? RESIDENCE_TYPES : MESS_TYPES;

  const [fees, setFees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [summary, setSummary] = useState({ receivable: 0, collected: 0, outstanding: 0, overdue: 0 });
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  // v24 — Edit + Delete state
  const [editFee, setEditFee] = useState(null);
  const [delFee, setDelFee] = useState(null);

  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const canEdit = ["admin", "accountant", "warden", "hostel_manager"].includes(user?.role);

  // v28 — Pagination + filter state for the fees table
  const [feePage, setFeePage] = useState(1);
  const [feeLimit, setFeeLimit] = useState(25);
  const [feeTotal, setFeeTotal] = useState(0);
  const [feePages, setFeePages] = useState(1);
  const [feeQ, setFeeQ] = useState("");
  const [feeStatus, setFeeStatus] = useState("all");
  const [feesLoading, setFeesLoading] = useState(false);

  // v28 — Pagination + filter state for the payments table
  const [payPage, setPayPage] = useState(1);
  const [payLimit, setPayLimit] = useState(25);
  const [payTotal, setPayTotal] = useState(0);
  const [payPages, setPayPages] = useState(1);
  const [payQ, setPayQ] = useState("");
  const [payMode, setPayMode] = useState("all");
  const [payFrom, setPayFrom] = useState("");
  const [payTo, setPayTo] = useState("");
  const [paysLoading, setPaysLoading] = useState(false);

  const [form, setForm] = useState({
    resident_id: "", resident_name: "",
    fee_type: types[0].v, amount: "",
    month: now.toLocaleString("en", { month: "long" }),
    year: String(now.getFullYear()),
    due_date: "",
  });
  const [pay, setPay] = useState({ amount: "", mode: "upi", reference: "" });

  const load = async () => {
    // Static: residents list + module summary (independent of pagination)
    const [r, dash] = await Promise.all([
      api.get("/residents"),
      api.get("/dashboard/stats").catch(() => ({ data: null })),
    ]);
    setResidents(r.data);
    if (dash.data?.fee_modules?.[module]) setSummary(dash.data.fee_modules[module]);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [module]);

  // v28 — Server-side paginated fees fetch. Reruns when filters/page change.
  const loadFees = async () => {
    setFeesLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(feePage), limit: String(feeLimit), module,
      });
      if (feeQ) params.set("q", feeQ);
      if (feeStatus !== "all") params.set("status", feeStatus);
      const { data } = await api.get(`/fees/paginated?${params}`);
      setFees(data.items || []);
      setFeeTotal(data.total || 0);
      setFeePages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFeesLoading(false); }
  };
  useEffect(() => { loadFees(); /* eslint-disable-next-line */ }, [module, feePage, feeLimit, feeQ, feeStatus]);
  useEffect(() => { setFeePage(1); }, [feeQ, feeStatus, module]);

  // v28 — Server-side paginated payments fetch.
  const loadPays = async () => {
    setPaysLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(payPage), limit: String(payLimit), module,
      });
      if (payQ) params.set("q", payQ);
      if (payMode !== "all") params.set("mode", payMode);
      if (payFrom) params.set("from_date", payFrom);
      if (payTo)   params.set("to_date", payTo);
      const { data } = await api.get(`/payments/paginated?${params}`);
      setPayments(data.items || []);
      setPayTotal(data.total || 0);
      setPayPages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPaysLoading(false); }
  };
  useEffect(() => { loadPays(); /* eslint-disable-next-line */ }, [module, payPage, payLimit, payQ, payMode, payFrom, payTo]);
  useEffect(() => { setPayPage(1); }, [payQ, payMode, payFrom, payTo, module]);

  // Called by every mutation that changes data — refreshes summary + both tables.
  const refreshAll = () => { load(); loadFees(); loadPays(); };

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/students/search?q=${encodeURIComponent(searchQ)}`)
        .then((r) => setSearchResults(r.data)).catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [searchQ]);

  const submitFee = async (e) => {
    e.preventDefault();
    if (!form.resident_id) { toast.error("Pick a resident"); return; }
    try {
      const period = form.month && form.year ? `${form.month} ${form.year}` : null;
      await api.post("/fees", {
        resident_id: form.resident_id, fee_type: form.fee_type, module,
        amount: parseFloat(form.amount), period,
        due_date: form.due_date || null,
      });
      toast.success(`${label} raised`); setOpen(false);
      setForm({ resident_id: "", resident_name: "", fee_type: types[0].v, amount: "",
        month: now.toLocaleString("en", { month: "long" }),
        year: String(now.getFullYear()), due_date: "" });
      setSearchQ(""); setSearchResults([]);
      refreshAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPay = (fee) => {
    setPayFor(fee);
    const balance = Math.max(0, Number(fee.amount) - Number(fee.paid_amount || 0));
    setPay({ amount: String(balance), mode: "upi", reference: "",
             // v30 — Default today, but the user must confirm/backdate.
             payment_date: new Date().toISOString().slice(0, 10) });
    setPayOpen(true);
  };
  const doPay = async (e) => {
    e.preventDefault();
    if (!pay.payment_date) { toast.error("Payment Received Date is required"); return; }
    try {
      await api.post("/payments", { fee_id: payFor.id, amount: parseFloat(pay.amount),
        mode: pay.mode, reference: pay.reference, payment_date: pay.payment_date });
      toast.success(`Payment recorded on ${pay.payment_date}`);
      setPayOpen(false); refreshAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadReceipt = async (pidOrPath) => {
    try {
      const path = pidOrPath.startsWith("combined/")
        ? `/payments/${pidOrPath}/receipt`
        : `/payments/${pidOrPath}/receipt`;
      const res = await fetch(`${API}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pidOrPath.startsWith("combined/")
        ? `${pidOrPath.split("/")[1]}.pdf`
        : `receipt-${pidOrPath}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Receipt download failed"); }
  };

  const feesRows = fees; // v28 — sorting is done server-side (created_at desc)

  return (
    <div className="space-y-6" data-testid={`${module}-fees-page`}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">{isResidence ? "Residence" : "Mess"}</div>
          <h1 className="font-display text-4xl font-bold mt-1">{label}</h1>
          <p className="text-muted-foreground mt-1">
            Independent ledger for {isResidence ? "rent, deposit, utilities, and residence penalties." : "monthly mess, per-meal charges, coupons, and guest meals."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadXlsx("fees").catch(()=>toast.error("Export failed"))}>
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid={`add-${module}-fee`}><Plus className="w-4 h-4 mr-2" /> New Fee</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl">Raise {label.slice(0, -1)}</DialogTitle></DialogHeader>
                <form onSubmit={submitFee} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Resident *</Label>
                    {form.resident_id ? (
                      <div className="border border-border rounded-md px-3 py-2 flex items-center justify-between">
                        <span className="text-sm">{form.resident_name}</span>
                        <Button size="sm" variant="ghost" type="button"
                          onClick={()=>setForm({...form, resident_id:"", resident_name:""})}>Change</Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <Input placeholder="Search by name, ID, phone…" value={searchQ}
                            onChange={(e)=>setSearchQ(e.target.value)} className="pl-9"
                            data-testid={`${module}-fee-search`} />
                        </div>
                        <div className="max-h-40 overflow-y-auto border border-border rounded-md">
                          {searchResults.length === 0 && <div className="text-xs text-muted-foreground p-3">Type to search…</div>}
                          {searchResults.map((r) => (
                            <button key={r.id} type="button"
                              onClick={()=>{setForm({...form, resident_id:r.id, resident_name:r.name}); setSearchQ(""); setSearchResults([]);}}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary">
                              {r.name} <span className="text-muted-foreground">· {r.phone || "—"}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select value={form.fee_type} onValueChange={(v)=>setForm({...form, fee_type:v})}>
                        <SelectTrigger data-testid={`${module}-fee-type`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {types.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount *</Label>
                      <Input required type="number" step="0.01" value={form.amount}
                        onChange={(e)=>setForm({...form, amount:e.target.value})}
                        data-testid={`${module}-fee-amount`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select value={form.month} onValueChange={(v)=>setForm({...form, month:v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{MONTHS.map((m)=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select value={form.year} onValueChange={(v)=>setForm({...form, year:v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{YEARS.map((y)=><SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Due</Label>
                      <Input type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date:e.target.value})} />
                    </div>
                  </div>
                  <DialogFooter><Button type="submit" data-testid={`${module}-fee-save`}>Save</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Receivable" amount={summary.receivable} />
        <SummaryCard label="Collected" amount={summary.collected} accent="text-emerald-700 bg-emerald-50 border-emerald-200" />
        <SummaryCard label="Outstanding" amount={summary.outstanding} accent="text-amber-700 bg-amber-50 border-amber-200" />
        <SummaryCard label="Overdue" amount={summary.overdue} accent="text-rose-700 bg-rose-50 border-rose-200" />
      </div>

      {/* Table */}
      <Card className="p-0 border-border bg-white overflow-hidden">
        {/* v28 — Filter bar (search + status) */}
        <div className="px-4 py-3 border-b border-border flex items-center gap-3 flex-wrap"
          data-testid={`${module}-fees-filters`}>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={feeQ} onChange={(e) => setFeeQ(e.target.value)}
              placeholder="Search resident / invoice / type…" className="pl-9 h-9"
              data-testid={`${module}-fees-search`} />
          </div>
          <Select value={feeStatus} onValueChange={setFeeStatus}>
            <SelectTrigger className="w-[180px] h-9" data-testid={`${module}-fees-status`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          {feesLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-3 px-4">Resident</th>
                <th className="py-3 pr-4">Type</th>
                <th className="py-3 pr-4">Period</th>
                <th className="py-3 pr-4">Due</th>
                <th className="py-3 pr-4 text-right">Amount</th>
                <th className="py-3 pr-4 text-right">Paid</th>
                <th className="py-3 pr-4 text-right">Balance</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {feesRows.map((f) => {
                const resident = residents.find((r) => r.id === f.resident_id);
                const bal = Math.max(0, Number(f.amount) - Number(f.paid_amount || 0));
                return (
                  <tr key={f.id} className="border-b border-border/50 hover:bg-secondary/40"
                    data-testid={`${module}-fee-${f.id}`}>
                    <td className="py-3 px-4">{resident?.name || "—"}</td>
                    <td className="py-3 pr-4 capitalize">{f.fee_type.replace(/_/g, " ")}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{f.period || "—"}</td>
                    <td className="py-3 pr-4 font-mono text-xs">{f.due_date || "—"}</td>
                    <td className="py-3 pr-4 text-right font-mono">₹{Number(f.amount).toLocaleString("en-IN")}</td>
                    <td className="py-3 pr-4 text-right font-mono text-emerald-700">₹{Number(f.paid_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="py-3 pr-4 text-right font-mono">
                      {bal > 0 ? <span className="text-rose-700">₹{bal.toLocaleString("en-IN")}</span> : "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <Badge variant={f.status === "paid" ? "default" : (f.urgency === "overdue" || f.overdue) ? "destructive" : (f.urgency === "due" || f.urgency === "due_soon") ? "outline" : "secondary"}
                        className={f.urgency === "due" ? "border-orange-500 text-orange-700 bg-orange-50" : f.urgency === "due_soon" ? "border-amber-400 text-amber-700 bg-amber-50" : undefined}>
                        {f.urgency === "overdue" || (f.overdue && f.status !== "paid") ? "OVERDUE"
                          : f.urgency === "due" ? "DUE TODAY"
                          : f.urgency === "due_soon" ? "DUE SOON"
                          : (f.status || "").replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <div className="inline-flex gap-1">
                        {canEdit && bal > 0 && (
                          <Button size="sm" variant="outline" onClick={() => openPay(f)}
                            data-testid={`pay-${f.id}`}>
                            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Pay
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => setEditFee(f)}
                            data-testid={`edit-fee-${f.id}`} title="Edit fee">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {["admin","accountant"].includes(user?.role) && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10"
                            onClick={() => setDelFee(f)}
                            data-testid={`delete-fee-${f.id}`} title="Delete fee">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {feesRows.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">
                  <IndianRupee className="w-8 h-8 mx-auto mb-2 opacity-40" /> No {label.toLowerCase()} yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 border-t border-border">
          <PaginationBar page={feePage} total={feeTotal} limit={feeLimit} totalPages={feePages}
            onPageChange={setFeePage} onLimitChange={(l) => { setFeeLimit(l); setFeePage(1); }} />
        </div>
      </Card>

      {/* Payments list */}
      <div>
        <div className="text-sm font-semibold mb-2">Recent {isResidence ? "Residence" : "Mess"} Payments</div>
        <Card className="p-0 border-border bg-white overflow-hidden">
          {/* v28 — payment filters: search / mode / date range */}
          <div className="px-4 py-3 border-b border-border flex items-center gap-3 flex-wrap"
            data-testid={`${module}-payments-filters`}>
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={payQ} onChange={(e) => setPayQ(e.target.value)}
                placeholder="Search receipt / reference…" className="pl-9 h-9"
                data-testid={`${module}-payments-search`} />
            </div>
            <Select value={payMode} onValueChange={setPayMode}>
              <SelectTrigger className="w-[150px] h-9" data-testid={`${module}-payments-mode`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="razorpay">Razorpay</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={payFrom} onChange={(e) => setPayFrom(e.target.value)}
              className="h-9 w-[150px]" title="From date" data-testid={`${module}-payments-from`} />
            <Input type="date" value={payTo} onChange={(e) => setPayTo(e.target.value)}
              className="h-9 w-[150px]" title="To date" data-testid={`${module}-payments-to`} />
            {(payFrom || payTo || payMode !== "all" || payQ) && (
              <Button variant="ghost" size="sm" className="h-9"
                onClick={() => { setPayFrom(""); setPayTo(""); setPayMode("all"); setPayQ(""); }}
                data-testid={`${module}-payments-clear`}>Clear</Button>
            )}
            {paysLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 px-4">Receipt No</th>
                  <th className="py-2 pr-4">Resident</th>
                  <th className="py-2 pr-4">Mode</th>
                  <th className="py-2 pr-4">Reference</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const resident = residents.find((r) => r.id === p.resident_id);
                  return (
                    <tr key={p.id} className="border-b border-border/50">
                      <td className="py-2 px-4 font-mono text-xs">
                        {p.receipt_no}
                        {p.group_receipt_no && (
                          <Badge variant="outline" className="ml-2 text-[10px]">COMBINED</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4">{resident?.name || p.resident_name || "—"}</td>
                      <td className="py-2 pr-4 capitalize">{p.mode?.replace("_", " ")}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{p.reference || "—"}</td>
                      <td className="py-2 pr-4 text-right font-mono">₹{Number(p.amount).toLocaleString("en-IN")}</td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">{p.created_at?.slice(0, 10)}</td>
                      <td className="py-2 pr-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => downloadReceipt(p.id)}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {payments.length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground text-xs">
                    {payQ || payMode !== "all" || payFrom || payTo ? "No payments match the filters." : "No payments yet."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 border-t border-border">
            <PaginationBar page={payPage} total={payTotal} limit={payLimit} totalPages={payPages}
              onPageChange={setPayPage} onLimitChange={(l) => { setPayLimit(l); setPayPage(1); }} />
          </div>
        </Card>
      </div>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          {payFor && (
            <form onSubmit={doPay} className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="eyebrow text-muted-foreground mb-1">Fee</div>
                  <div className="capitalize">{payFor.fee_type.replace(/_/g," ")} · {payFor.period || "—"}</div></div>
                <div><div className="eyebrow text-muted-foreground mb-1">Balance</div>
                  <div className="font-mono">₹{Math.max(0, Number(payFor.amount) - Number(payFor.paid_amount || 0)).toLocaleString("en-IN")}</div></div>
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input required type="number" step="0.01" value={pay.amount}
                  onChange={(e)=>setPay({...pay, amount:e.target.value})} data-testid="pay-amount" />
              </div>
              {/* v30 — Payment Received Date (required, backdatable) */}
              <div className="space-y-2">
                <Label>Payment Received Date * <span className="text-xs text-muted-foreground">(actual date — not today unless picked)</span></Label>
                <Input required type="date" value={pay.payment_date || ""}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
                  data-testid="pay-received-date" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={pay.mode} onValueChange={(v)=>setPay({...pay, mode:v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reference</Label>
                  <Input value={pay.reference} onChange={(e)=>setPay({...pay, reference:e.target.value})} />
                </div>
              </div>
              <DialogFooter><Button type="submit" data-testid="pay-save">Save</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* v24 — Edit Fee dialog */}
      {editFee && <EditFeeDialog fee={editFee} onClose={() => setEditFee(null)}
        onDone={() => { setEditFee(null); refreshAll(); }} />}

      {/* v24 — Delete Fee confirmation */}
      {delFee && <DeleteFeeDialog fee={delFee} onClose={() => setDelFee(null)}
        onDone={() => { setDelFee(null); refreshAll(); }} />}
    </div>
  );
}

function EditFeeDialog({ fee, onClose, onDone }) {
  const [f, setF] = useState({
    amount: String(fee.amount || ""),
    due_date: fee.due_date || "",
    period: fee.period || "",
    notes: fee.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/fees/${fee.id}`, {
        amount: Number(f.amount),
        due_date: f.due_date || null,
        period: f.period || null,
        notes: f.notes || null,
      });
      toast.success("Fee updated"); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-fee-dialog">
        <DialogHeader><DialogTitle>Edit Fee · {fee.invoice_no || fee.id.slice(0, 8)}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Amount (₹) *</Label>
              <Input required type="number" step="0.01" value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
                data-testid="edit-fee-amount" />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={f.due_date}
                onChange={(e) => setF({ ...f, due_date: e.target.value })}
                data-testid="edit-fee-due" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Period</Label>
              <Input value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Notes</Label>
              <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            v37: Amount can be corrected to a lower OR higher value. Payments stay unchanged.
            If the corrected amount is below already-paid (₹{fee.paid_amount || 0}), the excess is
            shown as an overpayment / credit adjustment.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-fee-save">
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteFeeDialog({ fee, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.delete(`/fees/${fee.id}`);
      toast.success("Fee deleted · related payments reversed");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="delete-fee-dialog">
        <DialogHeader><DialogTitle>Delete Fee · {fee.invoice_no || fee.id.slice(0, 8)}?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          This will delete the invoice (₹{Number(fee.amount).toLocaleString("en-IN")}) and reverse any
          payments already recorded against it. The resident's ledger, outstanding balance and dashboard
          totals will auto-recompute. This action cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={busy} onClick={confirm}
            data-testid="confirm-delete-fee">
            {busy ? "Deleting…" : "Delete Fee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, amount, accent = "" }) {
  return (
    <Card className={`p-4 border ${accent || "border-border bg-white"}`}>
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">
        ₹{Number(amount || 0).toLocaleString("en-IN")}
      </div>
    </Card>
  );
}
